import { BehaviorSubject, EMPTY } from 'rxjs';
import { distinctUntilChanged, switchMap, tap } from 'rxjs/operators';

export default class AuthRepository {
  auth$ = new BehaviorSubject(null);
  userWorkspace$ = new BehaviorSubject(null);
  isMediphanyWorkspace$ = new BehaviorSubject(false);

  subscribedUserChannel$ = new BehaviorSubject(null);

  constructor({ authService, socketService, emailChangeRepository, workspaceService, userRepository, patientRepository, casesRepository }) {
    this.authService = authService;
    this.socketService = socketService;
    this.workspaceService = workspaceService;
    this.userRepository = userRepository;
    this.patientRepository = patientRepository;
    this.casesRepository = casesRepository;
    this.emailChangeRepository = emailChangeRepository;
  }

  async login(username, password, recaptchaToken) {
    this.emailChangeRepository.dispose();

    this.authService.storeLastUpTime();
    const result = await this.authService.login(username, password, recaptchaToken);
    if (result?.workspaces?.length) {
      return result;
    }

    this.socketService.connect();
    return this.getProfile();
  }

  async loginToken(token) {
    this.emailChangeRepository.dispose();

    this.authService.storeLastUpTime();
    this.authService.loginToken(token);
    this.socketService.connect();
    return this.getProfile();
  }

  async register(data) {
    await this.authService.register(data);
  }

  async getIsMediphanyWorkspace() {
    const workspaceInfo = await this.authService.getIsMediphanyWorkspace();
    this.isMediphanyWorkspace$.next(workspaceInfo.isMediphanyWorkspace);
  }

  logout() {
    this.emailChangeRepository.dispose();
    this.unsubUserChannel();

    this.authService.logout();
    this.socketService.disconnect();
    this.auth$.next(null);
  }

  storeLastUpTime() {
    this.authService.storeLastUpTime();
  }

  checkUpTime() {
    return this.authService.checkUpTime();
  }

  async getWorkspace() {
    try {
      const userWorkspace = await this.workspaceService.getCurrentWorkspace();
      this.userWorkspace$.next(userWorkspace);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  }

  async getProfile() {
    const profile = await this.userRepository.getCurrentUser();
    const userWorkspace = await this.workspaceService.getCurrentWorkspace();
    const records = await this.patientRepository.getPatientRecords({ keyword: '', limit: 10, orderField: null, orderType: 'ASC', page: 0 });

    if (profile.role === 'patient') {
      await this.casesRepository.getNotPaidCases();
    }

    this.userRepository.setRequestedOrder(records?.data?.length > 0);
    this.userWorkspace$.next(userWorkspace);
    this.auth$.next(profile);

    this.unsubUserChannel();

    if (profile?.id) {
      this.subscribedUserChannel$.next(this.subscribeUserChannel().subscribe());
    }

    return profile;
  }

  unsubUserChannel() {
    const sub = this.subscribedUserChannel$.getValue();
    if (sub) {
      sub.unsubscribe();
      this.subscribedUserChannel$.next(null);
    }
  }

  async forgotPassword(data) {
    await this.authService.forgotPassword(data);
  }

  async validateInviteToken(token) {
    await this.authService.validateInviteToken(token);
  }

  async validateForgotToken(token) {
    await this.authService.validateForgotToken(token);
  }

  async setInvitePassword(token, password) {
    await this.authService.setInvitePassword(token, password);
  }

  async setForgotPassword(token, password, shouldLogin) {
    const result = await this.authService.setForgotPassword(token, password, shouldLogin);
    if (!shouldLogin) return null;

    if (result?.workspaces?.length) {
      return result;
    }

    this.socketService.connect();
    return this.getProfile();
  }

  subscribeUserChannel() {
    const profile = this.auth$.getValue();

    return this.socketService.connected$.pipe(
      distinctUntilChanged((a, b) => a === b),
      switchMap((connected) => {
        if (connected) {
          this.socketService.sendMessage('join-user-room');
          return this.socketService.getChannel('profile-update');
        }

        return EMPTY;
      }),
      tap(({ eventType, userId }) => {
        if (eventType === 'email-change' && `${userId}` === `${profile.id}`) {
          window.location.reload();
        }
      })
    );
  }
}
